import * as React from 'react';
import { NumericFormat } from 'react-number-format';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import { Input } from '@/components/forms/ui/input';

type MoneyInputProps = {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  onBlur?: () => void;
  min?: number;
  decimalScale?: number;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  placeholder?: string;
};

export function MoneyInput({
  value,
  onChange,
  onBlur,
  min,
  decimalScale = 2,
  disabled,
  id,
  placeholder,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid
}: MoneyInputProps) {
  const step = 1 / Math.pow(10, decimalScale);
  const resolvedPlaceholder = placeholder ?? `$0.${'0'.repeat(decimalScale)}`;
  const stepperLabel = ariaLabel ?? 'amount';

  const increment = (dir: 1 | -1) => {
    const base = value ?? 0;
    const next = roundTo(base + dir * step, decimalScale);
    const clamped = min !== undefined && next < min ? min : next;
    onChange(clamped);
  };

  if (disabled) {
    return (
      <div className="rounded border-[1px] border-gray-300 bg-gray-200 px-2 py-1.5 text-sm text-gray-400">
        {resolvedPlaceholder}
      </div>
    );
  }

  return (
    <div className="relative">
      <NumericFormat
        id={id}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        prefix="$"
        thousandSeparator
        decimalScale={decimalScale}
        fixedDecimalScale={false}
        allowNegative={false}
        value={value ?? ''}
        customInput={Input}
        placeholder={resolvedPlaceholder}
        inputMode="decimal"
        min={min}
        onValueChange={(vals, sourceInfo) => {
          if (sourceInfo.source !== 'event') return;
          onChange(vals.floatValue);
        }}
        onBlur={onBlur}
      />
      <div className="absolute inset-y-0 right-1 my-1 flex flex-col justify-center">
        <button
          type="button"
          onClick={() => increment(1)}
          className="text-muted-foreground hover:text-foreground flex h-4 w-6 items-center justify-center rounded-md"
          aria-label={`Increase ${stepperLabel}`}
          tabIndex={-1}>
          <FontAwesomeIcon icon={faChevronUp} className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={() => increment(-1)}
          className="text-muted-foreground hover:text-foreground flex h-4 w-6 items-center justify-center rounded-md"
          aria-label={`Decrease ${stepperLabel}`}
          tabIndex={-1}>
          <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function roundTo(n: number, scale: number) {
  return Number(Math.round(Number(`${n}e${scale}`)) + `e-${scale}`);
}
